import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { getUnreadCount } from '../api/notificationsApi'

/** Sayaç sorgusunun anahtarı — mutasyonlar bunu invalidate eder. */
export const UNREAD_COUNT_KEY = ['notifications', 'unread-count'] as const

/**
 * Bildirim sorgularının **ortak yoklama aralığı** (30 sn). Sayaç ve liste aynı ritimde tazelenir;
 * ikisi farklı aralıklarla dönseydi rozet ile panel içeriği arasında geçici tutarsızlık görünürdü.
 */
export const NOTIFICATIONS_POLL_MS = 30_000

/**
 * Okunmamış bildirim sayacı — zil rozetinin kaynağı (spec FR-007/FR-009).
 *
 * **Periyodik yenileme VARDIR: {@link NOTIFICATIONS_POLL_MS} (30 sn).** 058'in ilk sürümünde yoklama
 * bilinçle dışarıda bırakılmıştı (research.md D12: "ölçülmemiş maliyet, karar tersine çevrilebilir");
 * kullanım geri bildirimi bunu tersine çevirdi — kullanıcı yeni bildirimi görmek için **sayfayı
 * yenilemek zorunda kalmamalı**. Sorgu `SEND_ITEM` üzerinde tek satırlık bir `COUNT`'tur ve kullanıcı
 * başına dakikada iki isteğe denk gelir.
 *
 * <p>Sekme arka plandayken zamanlayıcı **durur** (`refetchIntervalInBackground` varsayılanı `false`),
 * bu yüzden açık bırakılmış ama bakılmayan sekmeler yük üretmez.
 *
 * Yoklamaya **ek olarak** olay bazlı tazeleme korunur:
 *
 * 1. **Sayfa ilk yüklendiğinde** — sorgunun kendi mount'u.
 * 2. **Rota değiştiğinde** — aşağıdaki `useEffect`. Bu gereklidir çünkü `NotificationsButton`
 *    `Topbar` içinde **kalıcı olarak mount'ludur**: sayfa değişse de bileşen yeniden mount olmaz,
 *    dolayısıyla `refetchOnMount` devreye girmez.
 * 3. **Sekmeye geri dönüldüğünde** — `refetchOnWindowFocus` (React Query varsayılanı) **kapatılmaz**;
 *    arka planda duran zamanlayıcının kaçırdığı süre böyle telafi edilir.
 * 4. **Panel açıldığında / okundu-okunmadı değiştirildiğinde / tümü okundu işaretlendiğinde** —
 *    ilgili bileşen ve mutasyonlar bu anahtarı invalidate eder; kullanıcı 30 sn beklemez.
 *
 * `staleTime` yoklama aralığıyla **aynı** tutulur: odak/mount kaynaklı tazelemeler de en fazla bu
 * sıklıkta olur, yani hiçbir tetikleyici 30 sn'lik ritmi aşan bir yük üretemez. (Invalidate eden
 * mutasyonlar `staleTime`'dan etkilenmez; onlar anında tazeler.)
 */
export function useUnreadCount() {
  const qc = useQueryClient()
  const { pathname } = useLocation()

  const query = useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: getUnreadCount,
    staleTime: NOTIFICATIONS_POLL_MS,
    refetchInterval: NOTIFICATIONS_POLL_MS,
    // Bu sorgu KULLANICI İSTEĞİYLE değil, arka planda çalışır ve rozetten başka bir yüzeyi yoktur;
    // başarısız olursa rozet son bilinen değerde kalır — bu doğru davranıştır. Merkezî hata hunisi
    // (019) susturulmazsa, ağ/sunucu kesintisinde yoklama HER 30 SANİYEDE bir hata toast'ı basardı
    // ve düğme Topbar'da kalıcı olduğu için bu tüm ekranlarda sürerdi.
    meta: { suppressGlobalError: true },
  })

  useEffect(() => {
    qc.invalidateQueries({ queryKey: UNREAD_COUNT_KEY })
  }, [pathname, qc])

  return query
}
